import type {
  ApiClient,
  ApiError,
  ApiRequestOptions,
  GetIdToken,
  GetLegacyToken,
  LegacyKuduRequestEnvelope,
  LegacyKuduResponseEnvelope,
} from './client.types';

interface CreateApiClientOptions {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  getIdToken?: GetIdToken;
  getLegacyToken?: GetLegacyToken;
  onAuthError?: () => void | Promise<void>;
}

function createApiError(
  kind: ApiError['kind'],
  message: string,
  status?: number,
): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = kind;
  error.status = status;
  return error;
}

async function readResponsePayload<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') ?? '';

  if (contentType.includes('application/json')) {
    return (await response.json()) as T;
  }

  return (await response.text()) as T;
}

interface ErrorAnswer {
  details?: Record<string, unknown>;
  message: string;
}

async function readErrorAnswer(response: Response): Promise<ErrorAnswer> {
  try {
    const payload = await readResponsePayload<unknown>(response);

    if (typeof payload === 'string' && payload.trim().length > 0) {
      return { message: payload };
    }

    if (
      payload &&
      typeof payload === 'object' &&
      'message' in payload &&
      typeof payload.message === 'string'
    ) {
      return {
        details: payload as Record<string, unknown>,
        message: payload.message,
      };
    }
  } catch {
    // Ignore response body parse failures and fall back to generic text below.
  }

  return { message: response.statusText || 'Request failed' };
}

async function readErrorMessage(response: Response): Promise<string> {
  return (await readErrorAnswer(response)).message;
}

function isLegacyKuduResponseEnvelope<T>(
  payload: unknown,
): payload is LegacyKuduResponseEnvelope<T> {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'Response' in payload
  );
}

function unwrapLegacyKuduResponseEnvelope<T>(
  payload: T | LegacyKuduResponseEnvelope<T>,
): T {
  if (isLegacyKuduResponseEnvelope<T>(payload)) {
    return payload.Response;
  }

  return payload;
}

function normalizeBody(body: ApiRequestOptions['body']) {
  const isReadableStream =
    typeof ReadableStream !== 'undefined' && body instanceof ReadableStream;

  if (
    body &&
    typeof body === 'object' &&
    !(body instanceof FormData) &&
    !(body instanceof Blob) &&
    !(body instanceof URLSearchParams) &&
    !(body instanceof ArrayBuffer) &&
    !isReadableStream
  ) {
    return JSON.stringify(body);
  }

  return body ?? undefined;
}

// Identifies the bundle this request came from. Read through a typeof guard
// because it is a build-time `define`: unit tests and any non-vite consumer
// evaluate this module without the replacement in place.
export const KUDU_BUILD_ID =
  typeof __KUDU_BUILD_ID__ === 'string' ? __KUDU_BUILD_ID__ : 'unknown';

export const KUDU_BUILD_HEADER = 'X-Kudu-Build';

// Stamps the build id on a Kudu API call so the backend's auth telemetry can
// tell which bundle a request came from.
//
// The signal that matters is its ABSENCE. Until issue #66 the service worker
// served its precached index.html until the user accepted an update, so a
// client could run a months-old bundle indefinitely — and those bundles predate
// the Firebase Bearer token entirely. A tokenless request with no build header
// is one of them; a tokenless request that DOES carry a build header is a live
// bug.
function withBuildHeader(headers: HeadersInit | undefined) {
  const merged = new Headers(headers);
  merged.set(KUDU_BUILD_HEADER, KUDU_BUILD_ID);
  return merged;
}

function mergeHeaders(
  initialHeaders: HeadersInit | undefined,
  body: ApiRequestOptions['body'],
  token: string | null,
  idempotencyKey?: string,
) {
  const headers = new Headers(initialHeaders);

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  if (idempotencyKey) {
    headers.set('X-Idempotency-Key', idempotencyKey);
  }

  if (
    body &&
    typeof body === 'object' &&
    !(body instanceof FormData) &&
    !headers.has('Content-Type')
  ) {
    headers.set('Content-Type', 'application/json');
  }

  return headers;
}

// Writes sent by any client that the server has not answered yet. A page that
// loads a new build at a page change waits for them (issue #66), so a save
// sent just before the user left a page is not cut off by the reload.
let writesInFlight = 0;

/** How many writes are on their way to the server. */
export function kuduWritesInFlight() {
  return writesInFlight;
}

async function countedIfWrite<T>(isWrite: boolean, send: () => Promise<T>): Promise<T> {
  if (!isWrite) {
    return send();
  }

  writesInFlight += 1;
  try {
    return await send();
  } finally {
    writesInFlight -= 1;
  }
}

export function createApiClient({
  baseUrl,
  fetchImpl = fetch,
  getIdToken,
  getLegacyToken,
  onAuthError,
}: CreateApiClientOptions = {}): ApiClient {
  function request<T>(
    path: string,
    options: ApiRequestOptions = {},
  ): Promise<T> {
    // A token expiring mid-session must not break a request: on the first 401
    // we force-refresh the Firebase ID token and retry once before treating it
    // as an auth failure.
    return countedIfWrite((options.method ?? 'GET') !== 'GET', () =>
      attempt<T>(path, options, false),
    );
  }

  async function attempt<T>(
    path: string,
    {
      auth = 'auto',
      body,
      headers,
      idempotencyKey,
      method = 'GET',
      ...rest
    }: ApiRequestOptions = {},
    forceRefresh = false,
  ): Promise<T> {
    try {
      const token =
        auth === 'none' || !getIdToken ? null : await getIdToken(forceRefresh);

      const url = path.startsWith('http') ? path : `${baseUrl ?? ''}${path}`;
      const response = await fetchImpl(url, {
        ...rest,
        body: normalizeBody(body),
        headers: mergeHeaders(headers, body, token, idempotencyKey),
        method,
      });

      if (response.ok) {
        return readResponsePayload<T>(response);
      }

      if (response.status === 401) {
        // First 401 with a refreshable token: refresh and retry once.
        if (!forceRefresh && auth !== 'none' && getIdToken) {
          return attempt<T>(
            path,
            { auth, body, headers, idempotencyKey, method, ...rest },
            true,
          );
        }
        await onAuthError?.();
        throw createApiError(
          'auth',
          (await readErrorMessage(response)) || 'Authentication required',
          response.status,
        );
      }

      const { details, message } = await readErrorAnswer(response);
      const refusal =
        response.status === 403
          ? createApiError('permission', message || 'Permission denied', response.status)
          : createApiError('data', message, response.status);

      if (details) {
        refusal.details = details;
      }

      throw refusal;
    } catch (error) {
      if (typeof error === 'object' && error !== null && 'kind' in error) {
        throw error;
      }

      throw createApiError(
        'network',
        error instanceof Error ? error.message : 'Network request failed',
      );
    }
  }

  function invokeKuduMethod<TResponse, TRequest = Record<string, never>>(
    methodName: string,
    requestPayload: TRequest,
    options: Pick<ApiRequestOptions, 'headers' | 'idempotencyKey'> = {},
  ): Promise<TResponse> {
    // Every method but the Get ones writes. It counts from the moment it is
    // called, before its tokens are ready.
    return countedIfWrite(!methodName.startsWith('Get'), async () => {
      const token = await getLegacyToken?.();

      if (!token) {
        await onAuthError?.();
        throw createApiError(
          'auth',
          'Legacy Kudu API token unavailable',
        );
      }

      const response = await attempt<
        TResponse | LegacyKuduResponseEnvelope<TResponse>
      >(
        `/_ah/KuduAPI/${methodName}`,
        {
          // Attach the verified Firebase ID token (Authorization: Bearer) — this
          // is the real identity the backend verifies. The body `Token` (md5) and
          // `user_id` are still sent during the transition but no longer
          // authorize.
          auth: 'auto',
          body: {
            Request: requestPayload,
            Token: token,
          } satisfies LegacyKuduRequestEnvelope<TRequest>,
          headers: withBuildHeader(options.headers),
          idempotencyKey: options.idempotencyKey,
          method: 'POST',
        },
        false,
      );

      return unwrapLegacyKuduResponseEnvelope(response);
    });
  }

  return {
    delete: <T>(path: string, options?: ApiRequestOptions) =>
      request<T>(path, { ...options, method: 'DELETE' }),
    get: <T>(path: string, options?: ApiRequestOptions) =>
      request<T>(path, { ...options, method: 'GET' }),
    invokeKuduMethod,
    patch: <T>(
      path: string,
      body?: ApiRequestOptions['body'],
      options?: ApiRequestOptions,
    ) => request<T>(path, { ...options, body, method: 'PATCH' }),
    post: <T>(
      path: string,
      body?: ApiRequestOptions['body'],
      options?: ApiRequestOptions,
    ) => request<T>(path, { ...options, body, method: 'POST' }),
    put: <T>(
      path: string,
      body?: ApiRequestOptions['body'],
      options?: ApiRequestOptions,
    ) => request<T>(path, { ...options, body, method: 'PUT' }),
    request,
  };
}

export const apiClient = createApiClient({
  baseUrl: import.meta.env.VITE_API_BASE_URL ?? '',
});
